import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import { inApp } from '../theme/uiScale'

export interface Box {
  left: number
  top: number
  width: number
  height: number
}

interface Options<K extends string | number> {
  scrollRef: RefObject<HTMLElement | null>
  /** Keys in display order; cards carry `data-k={String(key)}`. */
  keys: K[]
  picked: K[]
  onPick: (next: K[]) => void
  /** Off for people who may not change anything. */
  enabled: boolean
  /**
   * Touch is in select mode (taps toggle, sideways drags paint). Defaults to
   * "something is picked"; the mobile Select button turns it on with nothing picked.
   */
  selecting?: boolean
  /**
   * Picking content (the content picker): a mouse drag adds what it touches to
   * the selection, a drag that starts on a picked card takes them out, and a
   * click on the background keeps the selection.
   */
  additive?: boolean
}

interface TouchGesture<K> {
  id: number
  x: number
  y: number
  k0: K
  base: K[]
  mode: 'pending' | 'paint' | 'scroll' | 'done'
  /** Select mode was on when the finger went down. */
  sel: boolean
  add: boolean
  timer: number
  scroll: number
}

const LONG_PRESS_MS = 450
/** Finger travel that cancels a long press / decides drag vs scroll. */
const TOUCH_SLOP = 10
const MOUSE_SLOP = 5
/** Browsers follow a tap with emulated mouse events; ignore those for this long. */
const TOUCH_MOUSE_MS = 800
const EDGE = 40
const EDGE_SPEED = 14

/**
 * Picking cards in a scrolling grid by pointer (design `mDown` + `mPD`):
 *
 * - Mouse: press anywhere (on a card too) and drag a box; every card it
 *   touches is picked, Shift/Cmd/Ctrl adds to the selection. A press without
 *   movement is an ordinary click, and the click after a drag is swallowed.
 *   A plain click on the background clears the selection.
 * - Touch: a long press picks the card and starts selecting. While selecting,
 *   a tap toggles a card and a sideways drag paints a range (starting on a
 *   picked card un-picks it); a vertical move stays a scroll.
 *
 * The area auto-scrolls near its top and bottom edges while dragging.
 */
export function useGridSelect<K extends string | number>({ scrollRef, keys, picked, onPick, enabled, selecting: selectMode, additive: picking = false }: Options<K>) {
  const [box, setBox] = useState<Box | null>(null)
  const swallowClick = useRef(false)
  const lastTouch = useRef(0)
  const selecting = selectMode ?? picked.length > 0
  const latest = useRef({ keys, picked, onPick, selecting })
  latest.current = { keys, picked, onPick, selecting }

  const keyOf = (el: Element | null): K | undefined => {
    const k = el?.closest<HTMLElement>('[data-k]')?.dataset.k
    return latest.current.keys.find((x) => String(x) === k)
  }

  const autoScroll = (el: HTMLElement, clientY: number, then: () => void) => {
    const r = el.getBoundingClientRect()
    const vy = clientY > r.bottom - EDGE ? EDGE_SPEED : clientY < r.top + EDGE ? -EDGE_SPEED : 0
    if (!vy) return 0
    return window.setInterval(() => {
      el.scrollTop += vy
      then()
    }, 16)
  }

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      const el = scrollRef.current
      if (!enabled || !el || e.button !== 0) return
      // The mousedown a tap emulates must not reset the tap's swallowed click.
      if (Date.now() - lastTouch.current < TOUCH_MOUSE_MS) return
      if ((e.target as HTMLElement).closest('button, input, a, textarea, [role="menu"]')) return
      e.preventDefault()
      swallowClick.current = false
      const onBackground = !(e.target as HTMLElement).closest('[data-k]')
      const additive = picking || e.shiftKey || e.metaKey || e.ctrlKey
      const base = additive ? latest.current.picked : []
      const startKey = keyOf(e.target as Element)
      const removing = picking && startKey !== undefined && base.includes(startKey)
      const point = (ev: { clientX: number; clientY: number }) => {
        const r = el.getBoundingClientRect()
        // Screen distances in the scroller's own units (interface scale), like its scroll offsets.
        return { x: inApp(ev.clientX - r.left) + el.scrollLeft, y: inApp(ev.clientY - r.top) + el.scrollTop }
      }
      const start = point(e)
      let last: { clientX: number; clientY: number } = e
      let moved = false
      let timer = 0

      const update = () => {
        const p = point(last)
        const b = { left: Math.min(start.x, p.x), top: Math.min(start.y, p.y), width: Math.abs(start.x - p.x), height: Math.abs(start.y - p.y) }
        if (!moved && b.width < MOUSE_SLOP && b.height < MOUSE_SLOP) return
        moved = true
        const r = el.getBoundingClientRect()
        const hit: K[] = []
        el.querySelectorAll<HTMLElement>('[data-k]').forEach((card) => {
          const q = card.getBoundingClientRect()
          const x = inApp(q.left - r.left) + el.scrollLeft
          const y = inApp(q.top - r.top) + el.scrollTop
          const w = inApp(q.width)
          const h = inApp(q.height)
          const k = keyOf(card)
          if (k !== undefined && x < b.left + b.width && x + w > b.left && y < b.top + b.height && y + h > b.top) hit.push(k)
        })
        setBox(b)
        latest.current.onPick(removing ? base.filter((k) => !hit.includes(k)) : [...base, ...hit.filter((k) => !base.includes(k))])
      }
      const onMove = (ev: MouseEvent) => {
        last = ev
        window.clearInterval(timer)
        timer = autoScroll(el, ev.clientY, update)
        update()
      }
      const onUp = () => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
        window.clearInterval(timer)
        setBox(null)
        if (moved) swallowClick.current = true
        else if (onBackground && !additive) latest.current.onPick([])
      }
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scrollRef, enabled, picking],
  )

  // One touch gesture at a time.
  const touch = useRef<TouchGesture<K> | null>(null)

  const endTouch = () => {
    const t = touch.current
    if (t) {
      window.clearTimeout(t.timer)
      window.clearInterval(t.scroll)
    }
    touch.current = null
  }
  useEffect(() => endTouch, [])

  const onPointerDown = (e: React.PointerEvent) => {
    if (!enabled || e.pointerType === 'mouse') return
    lastTouch.current = Date.now()
    if ((e.target as HTMLElement).closest('button, input, a, textarea')) return
    const k0 = keyOf(e.target as Element)
    if (k0 === undefined) return
    endTouch()
    swallowClick.current = false
    const base = latest.current.picked
    const sel = latest.current.selecting
    const t: TouchGesture<K> = { id: e.pointerId, x: e.clientX, y: e.clientY, k0, base, mode: 'pending', sel, add: !base.includes(k0), timer: 0, scroll: 0 }
    touch.current = t
    if (!sel) {
      // Not selecting yet: holding still picks the card.
      t.timer = window.setTimeout(() => {
        if (touch.current !== t || t.mode !== 'pending') return
        t.mode = 'done'
        swallowClick.current = true
        latest.current.onPick([...latest.current.picked, k0])
        navigator.vibrate?.(10)
      }, LONG_PRESS_MS)
    }
  }

  const paintTo = (clientX: number, clientY: number) => {
    const t = touch.current
    if (!t) return
    const k = keyOf(document.elementFromPoint(clientX, clientY))
    if (k === undefined) return
    const ks = latest.current.keys
    const a = ks.indexOf(t.k0)
    const b = ks.indexOf(k)
    if (a < 0 || b < 0) return
    const range = ks.slice(Math.min(a, b), Math.max(a, b) + 1)
    latest.current.onPick(t.add ? [...t.base, ...range.filter((x) => !t.base.includes(x))] : t.base.filter((x) => !range.includes(x)))
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const t = touch.current
    if (!t || e.pointerId !== t.id || t.mode === 'scroll' || t.mode === 'done') return
    const dx = Math.abs(e.clientX - t.x)
    const dy = Math.abs(e.clientY - t.y)
    if (t.mode === 'pending') {
      if (t.sel && dx > TOUCH_SLOP && dx > dy) {
        t.mode = 'paint'
        window.clearTimeout(t.timer)
        try {
          ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
        } catch {
          /* capture is best effort */
        }
      } else if (dx > TOUCH_SLOP || dy > TOUCH_SLOP) {
        t.mode = 'scroll'
        window.clearTimeout(t.timer)
        return
      } else {
        return
      }
    }
    paintTo(e.clientX, e.clientY)
    const el = scrollRef.current
    if (el) {
      window.clearInterval(t.scroll)
      const { clientX, clientY } = e
      t.scroll = autoScroll(el, clientY, () => paintTo(clientX, clientY))
    }
  }

  const onPointerUp = (e: React.PointerEvent) => {
    const t = touch.current
    if (!t || e.pointerId !== t.id) return
    lastTouch.current = Date.now()
    if (t.mode === 'paint') swallowClick.current = true
    else if (t.mode === 'pending' && t.sel) {
      // A tap while selecting toggles instead of opening.
      swallowClick.current = true
      const cur = latest.current.picked
      latest.current.onPick(cur.includes(t.k0) ? cur.filter((x) => x !== t.k0) : [...cur, t.k0])
    }
    endTouch()
  }

  const onClickCapture = (e: React.MouseEvent) => {
    if (!swallowClick.current) return
    swallowClick.current = false
    e.stopPropagation()
    e.preventDefault()
  }

  // The long press must not also open the browser's context menu.
  const onContextMenu = (e: React.MouseEvent) => {
    if (touch.current || swallowClick.current) e.preventDefault()
  }

  return {
    box,
    selecting,
    rootProps: {
      onMouseDown,
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: endTouch,
      onClickCapture,
      onContextMenu,
      // While selecting, sideways finger drags paint instead of panning.
      style: enabled && selecting ? { touchAction: 'pan-y' as const } : undefined,
    },
  }
}
